import * as React from 'react';

import type { CguRegistreVersionRowDto } from '@/api/legal-admin-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  formatDate,
  formatDateTime,
  formatOpaqueId,
  stateBadgeVariant,
  stateLabel,
} from '@/routes/admin/cgu/cgu-format';

// =============================================================================
// cgu-versions-table.tsx — Table du REGISTRE des versions CGU (A-CGU ÉTAPE 2).
// =============================================================================
// 7 colonnes de cycle de vie GELÉES (versionNumber / état / effectiveDate / publishedAt /
// publishedBy / createdAt / createdBy) + nbSignatures. Tri client sur les colonnes clés
// (aria-sort). WCAG 2.1 AA : <table> + <caption> + <th scope>, libellés porteurs, l'état est
// un LIBELLÉ (Badge = renfort couleur uniquement). Les identités opaques sont tronquées
// (aucun endpoint de résolution nom — dégradation assumée).

/** Colonnes triables du registre. */
export type CguSortColumn =
  | 'versionNumber'
  | 'state'
  | 'effectiveDate'
  | 'publishedAt'
  | 'createdAt'
  | 'nbSignatures';

export type CguSortDirection = 'asc' | 'desc';

export interface CguVersionsTableProps {
  readonly rows: readonly CguRegistreVersionRowDto[];
  readonly sortColumn: CguSortColumn;
  readonly sortDirection: CguSortDirection;
  readonly onSort: (column: CguSortColumn) => void;
  readonly onOpen: (cguVersionId: string) => void;
  readonly onDownloadPdf: (cguVersionId: string) => void;
  /** Id de la version en cours de téléchargement PDF (spinner/disable), ou null. */
  readonly downloadingId?: string | null;
}

/** Compare deux valeurs pour le tri (chaînes / nombres / null → fin). */
function compare(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'fr');
}

function sortValue(
  row: CguRegistreVersionRowDto,
  column: CguSortColumn,
): string | number | null {
  return row[column];
}

export function CguVersionsTable({
  rows,
  sortColumn,
  sortDirection,
  onSort,
  onOpen,
  onDownloadPdf,
  downloadingId = null,
}: CguVersionsTableProps): React.JSX.Element {
  const sorted = React.useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const result = compare(sortValue(a, sortColumn), sortValue(b, sortColumn));
      return sortDirection === 'asc' ? result : -result;
    });
    return copy;
  }, [rows, sortColumn, sortDirection]);

  const ariaSort = (column: CguSortColumn): 'ascending' | 'descending' | 'none' =>
    sortColumn === column
      ? sortDirection === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none';

  const sortableHeader = (
    column: CguSortColumn,
    label: string,
  ): React.JSX.Element => (
    <th scope="col" aria-sort={ariaSort(column)} className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 rounded-sm font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {label}
        <span aria-hidden="true" className="text-xs">
          {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Registre des versions de CGU : numéro, état, dates de cycle de vie, auteurs,
          nombre de signatures et actions
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            {sortableHeader('versionNumber', 'Version')}
            {sortableHeader('state', 'État')}
            {sortableHeader('effectiveDate', 'Entrée en vigueur')}
            {sortableHeader('publishedAt', 'Publiée le')}
            <th scope="col" className="px-4 py-3 font-medium">
              Publiée par
            </th>
            {sortableHeader('createdAt', 'Créée le')}
            <th scope="col" className="px-4 py-3 font-medium">
              Créée par
            </th>
            {sortableHeader('nbSignatures', 'Signatures')}
            <th scope="col" className="px-4 py-3 font-medium">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={row.cguVersionId}
              className="border-b border-border last:border-b-0"
            >
              <td className="px-4 py-3 align-top font-mono">{row.versionNumber}</td>
              <td className="px-4 py-3 align-top">
                {/* Libellé porteur d'info ; le Badge n'est qu'un renfort couleur (ST-NFR-03). */}
                <Badge variant={stateBadgeVariant(row.state)}>
                  {stateLabel(row.state)}
                </Badge>
              </td>
              <td className="px-4 py-3 align-top">{formatDate(row.effectiveDate)}</td>
              <td className="px-4 py-3 align-top">{formatDateTime(row.publishedAt)}</td>
              <td
                className="px-4 py-3 align-top font-mono text-muted-foreground"
                aria-label={
                  row.publishedBy === null
                    ? 'Auteur de publication non disponible'
                    : `Auteur de publication (identifiant ${row.publishedBy})`
                }
              >
                {formatOpaqueId(row.publishedBy)}
              </td>
              <td className="px-4 py-3 align-top">{formatDateTime(row.createdAt)}</td>
              <td
                className="px-4 py-3 align-top font-mono text-muted-foreground"
                aria-label={
                  row.createdBy === null
                    ? 'Auteur de création non disponible'
                    : `Auteur de création (identifiant ${row.createdBy})`
                }
              >
                {formatOpaqueId(row.createdBy)}
              </td>
              <td className="px-4 py-3 align-top tabular-nums">{row.nbSignatures}</td>
              <td className="px-4 py-3 align-top">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onOpen(row.cguVersionId)}
                  >
                    Ouvrir
                  </Button>
                  {/* PDF visible SSI la version porte un PDF (Publiée/Archivée) — 404 sur Brouillon. */}
                  {row.hasPdf ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={downloadingId === row.cguVersionId}
                      onClick={() => onDownloadPdf(row.cguVersionId)}
                    >
                      {downloadingId === row.cguVersionId
                        ? 'Téléchargement…'
                        : 'PDF'}
                    </Button>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
